import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { api } from '@/services/api'

export interface User {
  id: string
  email: string
  name: string
  avatar_url: string | null
  is_active: boolean
  is_verified: boolean
  is_superuser: boolean
  last_login_at: string | null
  created_at: string | null
}

interface LoginResponse {
  user: User
  tokens: {
    access_token: string
    refresh_token: string
    token_type: string
  }
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name: string) => Promise<void>
  logout: () => void
  refreshAccessToken: () => Promise<void>
  setUser: (user: User) => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })

        try {
          // OAuth2 password flow requires form-urlencoded
          const formData = new URLSearchParams()
          formData.append('username', email)
          formData.append('password', password)

          const response = await api.post<LoginResponse>('/api/auth/login', formData, {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          })

          const { user, tokens } = response.data

          // Update API instance with new token
          api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access_token}`

          set({
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Login failed'
          const axiosError = error as { response?: { data?: { detail?: string } } }
          set({
            isLoading: false,
            error: axiosError.response?.data?.detail || message,
          })
          throw error
        }
      },

      register: async (email: string, password: string, name: string) => {
        set({ isLoading: true, error: null })

        try {
          const response = await api.post<LoginResponse>('/api/auth/register', {
            email,
            password,
            name,
          })

          const { user, tokens } = response.data

          // Update API instance with new token
          api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access_token}`

          set({
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Registration failed'
          const axiosError = error as { response?: { data?: { detail?: string } } }
          set({
            isLoading: false,
            error: axiosError.response?.data?.detail || message,
          })
          throw error
        }
      },

      logout: () => {
        // Call logout endpoint (fire and forget)
        api.post('/api/auth/logout').catch(() => {})

        delete api.defaults.headers.common['Authorization']
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        })
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        const response = await api.post<{ access_token: string; refresh_token: string }>(
          '/api/auth/refresh',
          { refresh_token: refreshToken }
        )

        const { access_token, refresh_token } = response.data

        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

        set({
          accessToken: access_token,
          refreshToken: refresh_token,
        })
      },

      setUser: (user: User) => {
        set({ user })
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

// Initialize API authorization header from stored token
const { accessToken } = useAuthStore.getState()
if (accessToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
}
